<div class="flex h-full">

  <!-- ── Main panel ──────────────────────────────────────────────────────────── -->
  <div class="flex min-w-0 flex-1 flex-col space-y-5 overflow-y-auto p-5">

    <!-- Header -->
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-sm font-semibold text-slate-900">System Log</h2>
        <p class="mt-0.5 text-xs text-slate-500">
          Structured operational events — dispatcher routing, worker execution, delivery outcomes.
          Redacted at write time. Not an audit log.
        </p>
      </div>

      <!-- 24h health pill -->
      <div *ngIf="health" class="flex flex-shrink-0 items-center gap-2 text-xs">
        <span class="text-slate-400">Last 24h:</span>
        <span class="rounded-full border px-2 py-0.5 font-semibold"
              [ngClass]="health.errorCount > 0 ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'">
          {{ health.errorCount > 0 ? health.errorCount + ' errors' : 'Healthy' }}
        </span>
        <span class="text-slate-400">{{ health.total }} events</span>
      </div>
      <span *ngIf="healthError" class="flex-shrink-0 text-xs text-slate-400">{{ healthError }}</span>
    </div>

    <!-- Filters -->
    <div class="flex flex-wrap items-center gap-2">

      <select
        [(ngModel)]="filterDays"
        (ngModelChange)="applyFilters()"
        class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
      >
        <option [value]="1">Last 24h</option>
        <option [value]="3">Last 3 days</option>
        <option [value]="7">Last 7 days</option>
      </select>

      <select
        [(ngModel)]="filterSeverity"
        (ngModelChange)="applyFilters()"
        class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
      >
        <option value="">All severities</option>
        <option *ngFor="let s of severities" [value]="s">{{ s }}</option>
      </select>

      <select
        [(ngModel)]="filterComponent"
        (ngModelChange)="applyFilters()"
        class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
      >
        <option value="">All components</option>
        <option *ngFor="let c of components" [value]="c">{{ c }}</option>
      </select>

      <input
        [(ngModel)]="filterEventType"
        (keyup.enter)="applyFilters()"
        placeholder="Event type…"
        class="w-36 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400"
      />

      <input
        [(ngModel)]="filterCorrelationId"
        (keyup.enter)="applyFilters()"
        placeholder="Correlation ID…"
        class="w-52 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-xs text-slate-700 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400"
      />

      <button
        (click)="applyFilters()"
        class="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
      >
        <lucide-icon name="search" [size]="11" [strokeWidth]="2"></lucide-icon>
        Search
      </button>

      <button
        (click)="load()"
        class="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
        title="Refresh"
      >
        <lucide-icon name="refresh-cw" [size]="11" [strokeWidth]="2"></lucide-icon>
        Refresh
      </button>
    </div>

    <!-- Summary strip -->
    <div *ngIf="!loading && events.length > 0" class="flex flex-wrap items-center gap-3 text-xs">
      <span class="text-slate-500">{{ events.length }} events</span>
      <span *ngIf="errorCount > 0" class="flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 font-semibold text-red-700">
        <lucide-icon name="triangle-alert" [size]="10" [strokeWidth]="2.5"></lucide-icon>
        {{ errorCount }} error{{ errorCount === 1 ? '' : 's' }}
      </span>
      <span *ngIf="warnCount > 0" class="flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
        {{ warnCount }} warn{{ warnCount === 1 ? '' : 's' }}
      </span>
    </div>

    <!-- Skeleton -->
    <div *ngIf="loading" class="space-y-2">
      <div *ngFor="let _ of [1,2,3,4,5,6,7,8]"
           class="h-10 animate-pulse rounded-lg bg-slate-100"></div>
    </div>

    <!-- Error -->
    <div *ngIf="!loading && error"
         class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <lucide-icon name="circle-x" [size]="16" [strokeWidth]="1.75"></lucide-icon>
      {{ error }}
    </div>

    <!-- Empty -->
    <div *ngIf="!loading && !error && events.length === 0"
         class="flex flex-col items-center justify-center py-16 text-center">
      <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-zinc-100">
        <lucide-icon name="scroll-text" [size]="22" class="text-zinc-400" [strokeWidth]="1.5"></lucide-icon>
      </div>
      <p class="mt-3 text-sm font-medium text-zinc-700">No events found</p>
      <p class="mt-1 text-xs text-zinc-400">Try widening the date range or clearing filters.</p>
    </div>

    <!-- Table -->
    <div *ngIf="!loading && !error && events.length > 0"
         class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <table class="min-w-full divide-y divide-slate-100 text-xs">
        <thead class="bg-slate-50">
          <tr>
            <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Time</th>
            <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Severity</th>
            <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Component</th>
            <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Provider</th>
            <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Event</th>
            <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Message</th>
            <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Status</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr
            *ngFor="let ev of events"
            (click)="openDrawer(ev)"
            class="cursor-pointer transition-colors hover:bg-slate-50"
            [class.bg-red-50]="ev.severity === 'ERROR' || ev.severity === 'CRITICAL'"
          >
            <td class="whitespace-nowrap px-3 py-2.5 font-mono text-slate-500" [title]="ev.timestamp">
              {{ relativeTime(ev.timestamp) }}
            </td>
            <td class="px-3 py-2.5">
              <span class="rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    [ngClass]="severityClass(ev.severity)">
                {{ ev.severity }}
              </span>
            </td>
            <td class="px-3 py-2.5">
              <span class="rounded-md border px-1.5 py-0.5 text-[10px] font-semibold"
                    [ngClass]="componentClass(ev.component)">
                {{ ev.component }}
              </span>
            </td>
            <td class="px-3 py-2.5 text-slate-500">{{ ev.provider ?? '—' }}</td>
            <td class="whitespace-nowrap px-3 py-2.5 font-mono text-slate-600">{{ ev.eventType }}</td>
            <td class="max-w-xs truncate px-3 py-2.5 text-slate-700" [title]="ev.message">
              {{ ev.message }}
            </td>
            <td class="px-3 py-2.5">
              <span class="rounded-md border px-1.5 py-0.5 text-[10px] font-semibold"
                    [ngClass]="statusClass(ev.status)">
                {{ ev.status }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
  <!-- /Main panel -->


  <!-- ── Detail drawer ────────────────────────────────────────────────────────── -->
  <div *ngIf="selectedEvent"
       class="relative flex w-[420px] flex-shrink-0 flex-col overflow-y-auto border-l border-slate-200 bg-white">

    <!-- Drawer header -->
    <div class="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-5 py-4">
      <div class="flex items-center gap-2">
        <span class="rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              [ngClass]="severityClass(selectedEvent.severity)">
          {{ selectedEvent.severity }}
        </span>
        <span class="font-mono text-xs text-slate-600">{{ selectedEvent.eventType }}</span>
      </div>
      <button
        (click)="closeDrawer()"
        class="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
      >
        <lucide-icon name="x" [size]="16" [strokeWidth]="2"></lucide-icon>
      </button>
    </div>

    <!-- Drawer body -->
    <div class="space-y-5 p-5">

      <!-- Message -->
      <div>
        <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Message</p>
        <p class="text-sm text-slate-800">{{ selectedEvent.message }}</p>
      </div>

      <!-- Error summary -->
      <div *ngIf="selectedEvent.errorSummary">
        <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-red-500">Error Summary</p>
        <p class="rounded-lg bg-red-50 px-3 py-2 font-mono text-xs text-red-800 whitespace-pre-wrap break-all">{{ selectedEvent.errorSummary }}</p>
      </div>

      <!-- Core fields -->
      <div class="overflow-hidden rounded-lg border border-slate-100">
        <table class="w-full text-xs">
          <tbody class="divide-y divide-slate-50">
            <tr class="bg-slate-50/50">
              <td class="w-28 px-3 py-2 font-semibold text-slate-500">Timestamp</td>
              <td class="px-3 py-2 font-mono text-slate-700">{{ selectedEvent.timestamp }}</td>
            </tr>
            <tr>
              <td class="px-3 py-2 font-semibold text-slate-500">Component</td>
              <td class="px-3 py-2">
                <span class="rounded border px-1.5 py-0.5 text-[10px] font-semibold" [ngClass]="componentClass(selectedEvent.component)">
                  {{ selectedEvent.component }}
                </span>
              </td>
            </tr>
            <tr class="bg-slate-50/50">
              <td class="px-3 py-2 font-semibold text-slate-500">Status</td>
              <td class="px-3 py-2">
                <span class="rounded border px-1.5 py-0.5 text-[10px] font-semibold" [ngClass]="statusClass(selectedEvent.status)">
                  {{ selectedEvent.status }}
                </span>
              </td>
            </tr>
            <tr *ngIf="selectedEvent.provider">
              <td class="px-3 py-2 font-semibold text-slate-500">Provider</td>
              <td class="px-3 py-2 text-slate-700">{{ selectedEvent.provider }}</td>
            </tr>
            <tr *ngIf="selectedEvent.tenantId" class="bg-slate-50/50">
              <td class="px-3 py-2 font-semibold text-slate-500">Tenant ID</td>
              <td class="px-3 py-2 font-mono text-slate-600 break-all">{{ selectedEvent.tenantId }}</td>
            </tr>
            <tr *ngIf="selectedEvent.orgId">
              <td class="px-3 py-2 font-semibold text-slate-500">Org ID</td>
              <td class="px-3 py-2 font-mono text-slate-600 break-all">{{ selectedEvent.orgId }}</td>
            </tr>
            <tr *ngIf="selectedEvent.resourceId" class="bg-slate-50/50">
              <td class="px-3 py-2 font-semibold text-slate-500">Resource</td>
              <td class="px-3 py-2 font-mono text-slate-600 break-all">{{ selectedEvent.resourceId }}</td>
            </tr>
            <tr *ngIf="selectedEvent.findingId">
              <td class="px-3 py-2 font-semibold text-slate-500">Finding ID</td>
              <td class="px-3 py-2 font-mono text-slate-600 break-all">{{ selectedEvent.findingId }}</td>
            </tr>
            <tr *ngIf="selectedEvent.deliveryId" class="bg-slate-50/50">
              <td class="px-3 py-2 font-semibold text-slate-500">Delivery ID</td>
              <td class="px-3 py-2 font-mono text-slate-600 break-all">{{ selectedEvent.deliveryId }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Correlation ID with copy -->
      <div *ngIf="selectedEvent.correlationId">
        <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Correlation ID</p>
        <div class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <code class="min-w-0 flex-1 break-all font-mono text-xs text-slate-700">{{ selectedEvent.correlationId }}</code>
          <button
            (click)="copyCorrelationId(selectedEvent.correlationId)"
            class="flex-shrink-0 rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            [title]="copied ? 'Copied!' : 'Copy correlation ID'"
          >
            <lucide-icon name="copy" [size]="13" [strokeWidth]="2"></lucide-icon>
          </button>
        </div>
        <p *ngIf="copied" class="mt-1 text-[10px] text-emerald-600">Copied!</p>
      </div>

      <!-- Metadata -->
      <div *ngIf="metadataKeys(selectedEvent.metadata).length > 0">
        <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Metadata</p>
        <div class="overflow-hidden rounded-lg border border-slate-100">
          <table class="w-full text-xs">
            <tbody class="divide-y divide-slate-50">
              <tr *ngFor="let key of metadataKeys(selectedEvent.metadata); let odd = odd"
                  [class.bg-slate-50]="odd">
                <td class="w-36 px-3 py-1.5 font-mono font-semibold text-slate-500">{{ key }}</td>
                <td class="px-3 py-1.5 font-mono text-slate-700 break-all">{{ selectedEvent.metadata[key] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Event ID -->
      <p class="text-[10px] text-slate-300">ID: {{ selectedEvent.id }}</p>

    </div>
  </div>
  <!-- /Drawer -->

</div>
